import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api`;

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' }
});

// Interceptor to add session token from localStorage as Authorization header
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  exchangeSession: (sessionId, role) => api.get(`/auth/session?session_id=${sessionId}&role=${role || 'candidate'}`),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  changePassword: (data) => api.put('/auth/change-password', data),
  deleteAccount: () => api.delete('/auth/delete-account'),
  exportData: () => api.get('/auth/export-data'),
  acceptPrivacy: () => api.post('/auth/accept-privacy'),
};

// Candidates
export const candidateAPI = {
  getProfile: () => api.get('/candidates/profile'),
  updateProfile: (data) => api.put('/candidates/profile', data),
  uploadCV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/candidates/cv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteCV: () => api.delete('/candidates/cv'),
  getCVUrl: (userId) => `${API}/candidates/${userId}/cv`,
  downloadCV: (userId) => api.get(`/candidates/${userId}/cv`, { responseType: 'blob' }),
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/candidates/photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deletePhoto: () => api.delete('/candidates/photo'),
  getPhotoUrl: (userId) => `${API}/candidates/${userId}/photo`,
  uploadVideo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/candidates/video', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteVideo: () => api.delete('/candidates/video'),
  getVideoUrl: (userId) => `${API}/candidates/${userId}/video`,
  search: (params) => api.get('/candidates/search', { params }),
  getById: (userId, viewContacts = false) => api.get(`/candidates/${userId}${viewContacts ? '?view_contacts=true' : ''}`),
  getAvailableNow: () => api.get('/candidates/available-now'),
  getCandidate: (userId) => api.get(`/candidates/${userId}`),
  geocodeSearch: (q) => api.get('/geocode/search', { params: { q } }),
};

// Hotels
export const hotelAPI = {
  getProfile: () => api.get('/hotels/profile'),
  updateProfile: (data) => api.put('/hotels/profile', data),
  completeOnboarding: (data) => api.post('/hotels/onboarding', data),
  getPlanStatus: () => api.get('/hotels/plan-status'),
};

// Favorites
export const favoritesAPI = {
  getAll: () => api.get('/favorites'),
  add: (candidateUserId) => api.post('/favorites', { candidate_user_id: candidateUserId }),
  remove: (candidateUserId) => api.delete(`/favorites/${candidateUserId}`),
};

// Hires
export const hiresAPI = {
  create: (data) => api.post('/hires', data),
  getAll: () => api.get('/hires'),
};

// Payments
export const paymentsAPI = {
  createCheckout: (data) => api.post('/payments/checkout', { ...data, origin_url: window.location.origin }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getFounderPromo: () => api.get('/promotions/premium-founder'),
  getCandidatePromo: () => api.get('/promotions/candidate-free-month'),
  claimCandidateFreeMonth: () => api.post('/promotions/candidate-free-month/claim'),
};

// AI Matching
export const matchingAPI = {
  getRecommendations: (data) => api.post('/matching/recommend', data),
};

// Messages
export const messagesAPI = {
  getConversations: () => api.get('/messages/conversations'),
  getMessages: (convId) => api.get(`/messages/conversations/${convId}`),
  deleteConversation: (convId) => api.delete(`/messages/conversations/${convId}`),
  sendMessage: (data) => api.post('/messages/send', data),
  createConversation: (candidateUserId) => api.post('/messages/conversations', { candidate_user_id: candidateUserId }),
  getUnreadCount: () => api.get('/messages/unread-count'),
};

// Profile Views
export const viewsAPI = {
  getStats: () => api.get('/candidates/views/stats'),
};

// Video Calls
export const callsAPI = {
  initiate: (conversationId) => api.post('/calls/initiate', { conversation_id: conversationId }),
  getPending: () => api.get('/calls/pending'),
  update: (callId, data) => api.put(`/calls/${callId}`, data),
  get: (callId) => api.get(`/calls/${callId}`),
};

// Admin
export const adminAPI = {
  getMetrics: () => api.get('/admin/metrics'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getRevenue: () => api.get('/admin/revenue'),
  getSubscriptions: () => api.get('/admin/subscriptions'),
  getHires: (params) => api.get('/admin/hires', { params }),
  toggleUser: (userId) => api.put(`/admin/users/${userId}/toggle`),
  getHotelsForVerification: (status) => api.get('/admin/hotels-for-verification', { params: { status: status || 'pending' } }),
  verifyHotel: (userId) => api.post(`/admin/hotels/${userId}/verify`),
  rejectHotel: (userId, reason) => api.post(`/admin/hotels/${userId}/reject`, { reason }),
  getAcquisitionFunnel: (params) => api.get('/admin/acquisition-funnel', { params }),
  // Prospect CRM
  getProspects: (params) => api.get('/admin/prospects', { params }),
  getProspectMetrics: () => api.get('/admin/prospects/metrics'),
  getProspect: (id) => api.get(`/admin/prospects/${id}`),
  createProspect: (data) => api.post('/admin/prospects', data),
  updateProspect: (id, data) => api.put(`/admin/prospects/${id}`, data),
  deleteProspect: (id) => api.delete(`/admin/prospects/${id}`),
  markContacted: (id) => api.post(`/admin/prospects/${id}/contact`),
  importCSVPreview: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/admin/prospects/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  confirmCSVImport: (rows) => api.post('/admin/prospects/import-csv/confirm', { rows }),
  syncProspectEvents: () => api.post('/admin/prospects/sync-events'),
  updateGoal: (goal) => api.put('/admin/prospects/goal', { goal }),
};

// Utility
export const utilAPI = {
  getPositions: () => api.get('/positions'),
  getPlans: () => api.get('/subscription-plans'),
};

// Job Postings
export const jobsAPI = {
  create: (data) => api.post('/jobs', data),
  getMyJobs: () => api.get('/jobs'),
  update: (jobId, data) => api.put(`/jobs/${jobId}`, data),
  close: (jobId) => api.post(`/jobs/${jobId}/close`),
  remove: (jobId) => api.delete(`/jobs/${jobId}`),
  // Public (no auth)
  getPublic: (params) => api.get('/jobs/public', { params }),
  getPublicBySlug: (slug) => api.get(`/jobs/public/${slug}`),
  getPublicStats: () => api.get('/jobs/public/stats'),
};

// Privacy
export const privacyAPI = {
  getConsents: () => api.get('/privacy/consents'),
  updateConsent: (purpose, status, policyVersion = "1.0") => api.post('/privacy/consents', { purpose, status, policy_version: policyVersion }),
  getDataSummary: () => api.get('/privacy/my-data-summary'),
  acceptTerms: (type, version) => api.post('/privacy/terms-acceptance', { type, version }),
};

export default api;
